import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('gmb_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    const storedToken = localStorage.getItem('gmb_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('gmb_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const headers = { 'Content-Type': 'application/json' };
    const trustedToken = localStorage.getItem('trusted_device_token');
    if (trustedToken) {
      headers['X-Trusted-Device'] = trustedToken;
    }
    const response = await axios.post(`${API}/auth/login`, { email, password }, { headers });
    const data = response.data;
    
    if (data.requires_2fa) {
      // Return 2FA required response - LoginPage handles the OTP step
      return data;
    }
    
    const { access_token, user: userData } = data;
    if (access_token) {
      localStorage.setItem('gmb_token', access_token);
      setToken(access_token);
      setUser(userData);
    }
    return data;
  };

  const register = async (name, email, password) => {
    // Check for referral code in localStorage (last-click attribution)
    let ref_code = null;
    const storedCode = localStorage.getItem('ref_code');
    const storedTimestamp = localStorage.getItem('ref_timestamp');
    if (storedCode && storedTimestamp) {
      const daysSinceClick = (Date.now() - parseInt(storedTimestamp)) / (1000 * 60 * 60 * 24);
      if (daysSinceClick <= 60) {
        ref_code = storedCode;
      }
    }

    const response = await axios.post(`${API}/auth/register`, { name, email, password, ref_code });

    // Clear referral code after registration
    localStorage.removeItem('ref_code');
    localStorage.removeItem('ref_timestamp');
    
    // New flow: registration returns requires_verification, no token
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('gmb_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
